import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Sparkles, BookMarked, ArrowRight, ShieldCheck, MapPin, FileCheck,
} from "lucide-react";

/**
 * BookBurocraziaSection
 * ─────────────────────
 * Companion launch block for "MEDICO IN ITALIA · La guida alla burocrazia",
 * released June 1, 2026. Sells alongside the bigger 2-tomi exam guide.
 *
 *  - Hero-style intro banner with the new cover (the Roma + flag art).
 *  - Soft-light theme so it CONTRASTS with the dark BookLaunchSection that
 *    sits right below (visual variety = more attention).
 *  - Single CTA: paperback in Italian on Amazon.
 *
 * Designed to be the FIRST book block users see on the public landing,
 * because it answers the very first question a foreign doctor has:
 * "okay, dove comincio con la burocrazia?".
 */
const BURO_COVER =
  "https://customer-assets.emergentagent.com/job_italian-med-test/artifacts/2lgjur38_ChatGPT%20Image%201%20jun%202026%2C%2004_23_23%20p.m..png";

const BURO_AMAZON_URL = "https://amzn.eu/d/0eruU1R5";

// Italian flag palette tuned for a light/cream surface (contrasts with the
// dark BookLaunchSection that follows below).
const C = {
  green:    "#2F8F5C",
  red:      "#C9314A",
  greenSoft:"rgba(47,143,92,0.10)",
  redSoft:  "rgba(201,49,74,0.10)",
  ink:      "#1A1F2B",
  muted:    "#5C6373",
  cream:    "#FAF7F0",
  surface:  "#FFFFFF",
  border:   "rgba(26,31,43,0.10)",
};

const FADE_UP = {
  hidden: { opacity: 0, y: 24 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};
const VIEWPORT = { once: true, margin: "-80px" };

// JSON-LD schema for THIS book (separate from the big guide).
function BurocraziaSchema() {
  useEffect(() => {
    const id = "book-burocrazia-schema";
    if (document.getElementById(id)) return;
    const s = document.createElement("script");
    s.id = id;
    s.type = "application/ld+json";
    s.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Book",
      "name": "MEDICO IN ITALIA: La guida definitiva per il riconoscimento del titolo senza perdere anni nella burocrazia",
      "author": { "@type": "Person", "name": "Dr. Lucas Pablo Terzano" },
      "bookFormat": "https://schema.org/Paperback",
      "inLanguage": "it",
      "image": BURO_COVER,
      "datePublished": "2026-06-01",
      "description":
        "Guida pratica per medici stranieri che vogliono ottenere il riconoscimento del titolo in Italia. Documenti, traduzioni, apostille, D1-D2, PEC, Albo dei Medici, esame di Roma · passo dopo passo.",
      "url": BURO_AMAZON_URL,
      "offers": {
        "@type": "Offer",
        "url": BURO_AMAZON_URL,
        "availability": "https://schema.org/InStock",
        "priceCurrency": "EUR",
      },
    });
    document.head.appendChild(s);
    return () => { s.remove(); };
  }, []);
  return null;
}

function FlagStripe() {
  return (
    <div className="flex h-1 w-20 overflow-hidden rounded-full" aria-hidden="true">
      <span className="flex-1" style={{ background: C.green }} />
      <span className="flex-1" style={{ background: "#FFF" }} />
      <span className="flex-1" style={{ background: C.red }} />
    </div>
  );
}

function PrimaryCTA() {
  return (
    <a
      href={BURO_AMAZON_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="book-burocrazia-amazon-cta"
      className="group inline-flex items-center justify-center gap-2 rounded-xl px-7 py-3.5 font-semibold text-base transition-all hover:scale-[1.02] hover:shadow-[0_18px_40px_-12px_rgba(201,49,74,0.45)]"
      style={{
        background: `linear-gradient(135deg, ${C.red} 0%, #E04B66 50%, ${C.red} 100%)`,
        color: "#FFF",
        boxShadow: "0 12px 28px -10px rgba(201,49,74,0.45)",
      }}
    >
      <BookMarked className="h-4 w-4" />
      Acquista la guida su Amazon
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
    </a>
  );
}

// Highlight pills that quickly convey what the book solves.
const HIGHLIGHTS = [
  { icon: FileCheck,   text: "Documenti, traduzioni, apostille" },
  { icon: ShieldCheck, text: "Iscrizione all'Albo dei Medici" },
  { icon: MapPin,      text: "Fino all'esame di Roma" },
];

// ─── Section ────────────────────────────────────────────────────────────────
export default function BookBurocraziaSection() {
  return (
    <section
      id="libro-burocrazia"
      aria-label="Novità giugno 2026 · MEDICO IN ITALIA · Guida alla burocrazia"
      data-testid="book-burocrazia-section"
      className="relative py-20 sm:py-28"
      style={{
        background:
          "linear-gradient(180deg, #FAF7F0 0%, #F4F1E8 100%)",
      }}
    >
      <BurocraziaSchema />

      {/* Decorative soft circles for depth */}
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          backgroundImage:
            "radial-gradient(circle at 12% 18%, rgba(47,143,92,0.10) 0%, transparent 35%), radial-gradient(circle at 88% 80%, rgba(201,49,74,0.10) 0%, transparent 38%)",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-6xl mx-auto px-6 sm:px-8">
        {/* Top-of-section banner */}
        <motion.div
          initial={FADE_UP.hidden}
          whileInView={FADE_UP.show}
          viewport={VIEWPORT}
          className="flex items-center justify-center gap-2 mb-12"
        >
          <span
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[11px] tracking-[0.3em] font-bold"
            style={{
              background: C.surface,
              color: C.red,
              border: `1px solid ${C.redSoft}`,
              boxShadow: "0 4px 14px -4px rgba(201,49,74,0.18)",
            }}
          >
            <Sparkles className="h-3 w-3" />
            NOVITÀ GIUGNO 2026 · NUOVO LIBRO
          </span>
        </motion.div>

        <div className="grid gap-10 lg:gap-14 lg:grid-cols-[0.95fr_1.05fr] items-center">
          {/* Cover */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92, rotateY: 22 }}
            whileInView={{ opacity: 1, scale: 1, rotateY: 6 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto w-full max-w-[420px]"
            style={{ perspective: "1400px" }}
          >
            <div
              className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-[80%] h-12 blur-3xl"
              style={{ background: `radial-gradient(ellipse, ${C.redSoft}, transparent 70%)` }}
              aria-hidden="true"
            />
            <img
              src={BURO_COVER}
              alt="MEDICO IN ITALIA · La guida alla burocrazia · Riconoscimento, documenti e pratiche passo dopo passo fino all'esame di Roma · Dr. Lucas Pablo Terzano"
              loading="lazy"
              data-testid="book-burocrazia-cover"
              className="relative w-full rounded-lg shadow-[0_30px_60px_-15px_rgba(0,0,0,0.4)]"
              style={{
                transform: "rotateY(-4deg) rotateX(2deg)",
                border: "1px solid rgba(26,31,43,0.18)",
              }}
            />
            <div
              className="absolute top-0 right-[6%] h-full w-[3px] rounded-sm pointer-events-none"
              style={{
                background:
                  "linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)",
                transform: "rotateY(-4deg)",
              }}
              aria-hidden="true"
            />
          </motion.div>

          {/* Copy */}
          <motion.div initial={FADE_UP.hidden} whileInView={FADE_UP.show} viewport={VIEWPORT}>
            <FlagStripe />
            <div
              className="mt-4 text-[10px] tracking-[0.32em] font-bold mb-3"
              style={{ color: C.green }}
            >
              IN VENDITA · 1 GIUGNO 2026
            </div>
            <h2
              className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.08] font-semibold"
              style={{ color: C.ink }}
            >
              <span style={{ color: C.ink }}>MEDICO IN ITALIA</span>:<br />
              <em className="italic font-medium" style={{ color: C.green }}>
                La guida alla burocrazia
              </em>
            </h2>
            <p
              className="mt-5 text-base sm:text-lg leading-relaxed"
              style={{ color: C.muted }}
            >
              Senza bugie, senza giri di parole.{" "}
              <strong style={{ color: C.ink }}>
                Riconoscimento, documenti e pratiche passo dopo passo
              </strong>{" "}
              fino all&apos;esame di Roma · scritto da chi ci è già passato.
            </p>

            <ul className="mt-6 space-y-2.5" data-testid="book-burocrazia-highlights">
              {HIGHLIGHTS.map((h) => {
                const Icon = h.icon;
                return (
                  <li
                    key={h.text}
                    className="flex items-center gap-3 text-sm"
                    style={{ color: C.ink }}
                  >
                    <span
                      className="inline-flex h-7 w-7 items-center justify-center rounded-lg shrink-0"
                      style={{
                        background: C.greenSoft,
                        border: `1px solid ${C.green}30`,
                      }}
                    >
                      <Icon className="h-3.5 w-3.5" style={{ color: C.green }} />
                    </span>
                    <span>{h.text}</span>
                  </li>
                );
              })}
            </ul>

            <p
              className="mt-6 text-[13px] leading-relaxed italic"
              style={{ color: C.muted }}
            >
              Scritto dal Dr. Lucas Pablo Terzano, medico radiologo argentino
              che ha affrontato personalmente il riconoscimento del titolo
              in Italia e oggi esercita la professione nel Paese.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <PrimaryCTA />
              <span
                className="inline-flex items-center gap-1.5 text-xs"
                style={{ color: C.muted }}
              >
                <BookMarked className="h-3.5 w-3.5" style={{ color: C.green }} />
                Solo copertina morbida · italiano
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
